/* Kkumi Rewards client cache — one shared, version-gated cache for every
 * storefront surface that reads the rewards App Proxy (/apps/rewards/*), so the
 * same data isn't refetched on every page when nothing has changed.
 *
 * Exposed as window.KKRewards. Adopted by kkumi-club.js (club page + cart
 * wallet + footer card), kkumi-earn-points.js, kkumi-promo-banner.js and
 * kkumi-club-launcher.js. Any other surface can opt in via KKRewards.get().
 *
 * HOW IT STAYS CORRECT (no stale money data):
 *  • PUBLIC reads (/program, /campaigns, /bias-directory) carry no customer
 *    data and change rarely → cached in sessionStorage with a short TTL.
 *  • MEMBER reads are gated on a cheap **version probe**: exactly one /balance
 *    fetch per page load establishes `balance:lifetime_points`. While that
 *    version is unchanged from the last load, member reads (/codes, /options,
 *    /history, /referral, …) are served from cache — no network. The moment the
 *    version differs (they earned/spent points, got a reward), the member cache
 *    is dropped and those reads refetch. So the gate is authoritative *before*
 *    anything is served — callers never render stale balances.
 *  • Any mutation (POST/PUT/DELETE) calls invalidate() so the next read
 *    re-probes and refetches.
 *
 * Storage is sessionStorage (per tab); private-mode / quota failures fall back
 * to plain network fetches. Errors propagate with a `.code` like the callers'
 * own `api()` helpers, so behaviour is unchanged on failure.
 */
window.KKRewards = (function () {
  'use strict';

  var MEM_KEY = 'kk:rw:m:v1';   // { ver, entries: { path: { data, at } } }
  var PUB_KEY = 'kk:rw:p:v1';   // { path: { data, at } }
  var PUBLIC = { '/program': 1, '/campaigns': 1, '/bias-directory': 1 };
  var MEMBER_TTL = 5 * 60 * 1000;   // soft backstop even when version matches
  var PUBLIC_TTL = 10 * 60 * 1000;

  var memVer = null;     // current member version this page load
  var readyP = null;     // memoised version probe

  function base() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + '/apps/rewards';
  }
  function now() { return Date.now(); }
  function read(k) { try { return JSON.parse(sessionStorage.getItem(k) || 'null') || {}; } catch (e) { return {}; } }
  function write(k, v) { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* quota / private mode */ } }
  function drop(k) { try { sessionStorage.removeItem(k); } catch (e) {} }

  function fetchJSON(path, opts) {
    opts = opts || {};
    return fetch(base() + path, {
      method: opts.method || 'GET',
      headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      credentials: 'same-origin',
    }).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) { var e = new Error(j.error || ('HTTP ' + r.status)); e.code = j.error; throw e; }
        return j;
      });
    });
  }

  // The version probe: one /balance fetch per page load. Resets the member
  // cache when the version changed since the previous load. Also caches the
  // /balance payload itself so callers reading /balance get this exact result.
  function ready() {
    if (readyP) return readyP;
    readyP = fetchJSON('/balance').then(function (b) {
      var ver = (b && b.logged_in) ? (String(b.balance) + ':' + String(b.lifetime_points)) : 'guest';
      var store = read(MEM_KEY);
      if (store.ver !== ver) store = { ver: ver, entries: {} };  // something changed → drop member cache
      store.entries = store.entries || {};
      store.entries['/balance'] = { data: b, at: now() };
      write(MEM_KEY, store);
      memVer = ver;
      return b;
    }).catch(function (e) {
      // Probe failed (offline / proxy hiccup): keep serving last-known cache.
      memVer = read(MEM_KEY).ver || null;
      throw e;
    });
    return readyP;
  }

  function getPublic(path, opts) {
    var ttl = opts.ttl != null ? opts.ttl : PUBLIC_TTL;
    var store = read(PUB_KEY);
    var e = store[path];
    if (e && !opts.force && (now() - e.at) < ttl) return Promise.resolve(e.data);
    return fetchJSON(path, opts).then(function (d) {
      var s = read(PUB_KEY); s[path] = { data: d, at: now() }; write(PUB_KEY, s);
      return d;
    });
  }

  function getMember(path, opts) {
    return ready().then(function (probe) {
      if (path === '/balance' && !opts.force) return probe;
      var store = read(MEM_KEY); store.entries = store.entries || {};
      var e = store.entries[path];
      var ttl = opts.ttl != null ? opts.ttl : MEMBER_TTL;
      if (e && !opts.force && (now() - e.at) < ttl) return e.data;
      return fetchJSON(path, opts).then(function (d) {
        var s = read(MEM_KEY); s.ver = memVer; s.entries = s.entries || {};
        s.entries[path] = { data: d, at: now() }; write(MEM_KEY, s);
        return d;
      });
    }).catch(function (err) {
      // If the probe failed, still try a direct fetch so the surface works.
      if (path === '/balance') throw err;
      var store = read(MEM_KEY); var e = (store.entries || {})[path];
      if (e) return e.data;
      return fetchJSON(path, opts);
    });
  }

  /** GET a rewards endpoint through the cache.
   *  opts: { scope:'public'|'member', ttl, force } — scope defaults by path. */
  function get(path, opts) {
    opts = opts || {};
    var scope = opts.scope || (PUBLIC[path] ? 'public' : 'member');
    return scope === 'public' ? getPublic(path, opts) : getMember(path, opts);
  }

  /** Synchronous last-known value (for instant first paint), or null. */
  function peek(path) {
    if (PUBLIC[path]) { var p = read(PUB_KEY)[path]; return p ? p.data : null; }
    var m = (read(MEM_KEY).entries || {})[path]; return m ? m.data : null;
  }

  /** Drop the member cache + force a re-probe next read (call after a mutation). */
  function invalidate() { drop(MEM_KEY); memVer = null; readyP = null; }

  /** Raw, uncached fetch (same shape) for non-GET calls or explicit bypass. */
  return { get: get, peek: peek, ready: ready, invalidate: invalidate, fetch: fetchJSON, base: base };
})();
